import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Search, Users, Briefcase, Coins, ArrowRightLeft, IdCard } from "lucide-react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";

const ICONS = {
  players: Users, employees: Briefcase, transactions: Coins,
  transfers: ArrowRightLeft, members: IdCard,
};
const LABELS = {
  players: "Joueurs", employees: "Employés", transactions: "Transactions",
  transfers: "Transferts", members: "Membres",
};
const ROUTES = {
  players: "/app/players", employees: "/app/employees", transactions: "/app/finances",
  transfers: "/app/transfers", members: "/app/members",
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (!q || q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search`, { params: { q } });
        setResults(data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const total = results ? Object.values(results).reduce((a, b) => a + b.length, 0) : 0;

  const handleSelect = (key) => {
    setOpen(false);
    setQ("");
    setResults(null);
    navigate(ROUTES[key]);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 border border-[#E0E0E0] rounded-md text-sm text-[#616161] hover:border-[#C62828]/40 hover:text-[#121212] transition w-72"
        data-testid="global-search-trigger"
      >
        <Search className="w-3.5 h-3.5" />
        <span>Rechercher...</span>
        <kbd className="ml-auto text-[10px] bg-[#F4F5F7] px-1.5 py-0.5 rounded font-mono">Ctrl K</kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl p-0 overflow-hidden" data-testid="global-search-dialog">
          <VisuallyHidden><DialogTitle>Recherche globale</DialogTitle></VisuallyHidden>
          <div className="flex items-center gap-2 px-4 border-b border-[#E0E0E0]">
            <Search className="w-4 h-4 text-[#616161]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Rechercher joueurs, employés, transactions..."
              className="flex-1 h-12 outline-none text-sm bg-transparent"
              data-testid="global-search-input"
            />
            {loading && <span className="text-xs text-[#616161]">…</span>}
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {q.length < 2 ? (
              <p className="text-sm text-[#616161] p-4">Tapez au moins 2 caractères pour rechercher.</p>
            ) : !results || total === 0 ? (
              <p className="text-sm text-[#616161] p-4">Aucun résultat pour « {q} ».</p>
            ) : (
              Object.entries(results).map(([key, items]) => items.length > 0 && (
                <div key={key} className="mb-2">
                  <p className="px-3 py-1 text-[10px] tracking-overline text-[#616161]">{LABELS[key]}</p>
                  {items.map((it) => {
                    const Icon = ICONS[key];
                    return (
                      <button key={it.id} onClick={() => handleSelect(key)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-[#F4F5F7] text-left text-sm"
                        data-testid={`search-result-${key}-${it.id}`}>
                        <Icon className="w-4 h-4 text-[#C62828]" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{it.label}</p>
                          <p className="text-xs text-[#616161] truncate">{it.sub}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
